import React from "react";
import { Alert, AlertDescription, AlertIcon, Box, Button, HStack, Text } from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import { getReportTypeDisplayLabel } from "../../utils/reportTypeRules";

const POLICY_REPORT_TYPES = ["market-context-performance", "trade-management"] as const;

export type StrategyComposeNoPolicyPromptProps = {
  strategyId: string;
  subtleText: string;
  textColor: string;
  onNavigateAway?: () => void;
};

export function StrategyComposeNoPolicyPrompt({
  strategyId,
  subtleText,
  textColor,
  onNavigateAway,
}: StrategyComposeNoPolicyPromptProps): React.ReactElement {
  const history = useHistory();

  const openGenerateReport = (reportType: (typeof POLICY_REPORT_TYPES)[number]) => {
    onNavigateAway?.();
    const params = new URLSearchParams({
      strategy_id: strategyId,
      report_type: reportType,
    });
    history.push(`/reports/new?${params.toString()}`);
  };

  return (
    <Box>
      <Alert status="info" borderRadius="8px" fontSize="sm" mb={4}>
        <AlertIcon />
        <AlertDescription>
          <Text fontWeight="medium" color={textColor} mb={1}>
            No policy versions yet
          </Text>
          <Text color={subtleText}>
            Compose needs a saved policy from a report run. Link this strategy, then generate a{" "}
            <Text as="span" fontWeight="medium" color={textColor}>
              {getReportTypeDisplayLabel("market-context-performance")}
            </Text>{" "}
            or{" "}
            <Text as="span" fontWeight="medium" color={textColor}>
              {getReportTypeDisplayLabel("trade-management")}
            </Text>{" "}
            report. Tessera will store a policy version you can attach here.
          </Text>
        </AlertDescription>
      </Alert>
      <Text fontSize="sm" color={subtleText} mb={3}>
        Create a report with this strategy:
      </Text>
      <HStack spacing={2} flexWrap="wrap">
        {POLICY_REPORT_TYPES.map((key) => (
          <Button
            key={key}
            size="sm"
            bgGradient="linear(135deg, #38BBD1, #3EEAEC)"
            color="white"
            _hover={{ opacity: 0.95 }}
            onClick={() => openGenerateReport(key)}
          >
            {getReportTypeDisplayLabel(key)} report
          </Button>
        ))}
        <Button size="sm" variant="outline" onClick={() => history.push("/reports")}>
          View reports
        </Button>
      </HStack>
    </Box>
  );
}
